/**
 * 司机档案页面
 */
import React, { useEffect, useState } from 'react';
import { Card, Form, Row, Col, Input, Button, Select, Space, Image } from 'antd';

import { useTable } from '@/utils/useTable';
import CommonTable from '@/components/commonTable';
import styles from '@/styles/pageContent.module.scss';
import DriverDetailModal from '@/components/driverDetailModal';
import LinkFaceLibModal from '@/components/linkFaceLibModal';
import { getDriverArchiveList, updateDriverArchiveInfo } from '@/service/driverArchive';
import { useFleetOptions } from './hooks/useFleetOptions.hook';
import { message } from 'antd';
import { tagListRequest } from '@/service/tagManage';
import { TagCategoryEnum } from '@/constants/tagManage';

const { Option } = Select;

/**
 * 司机档案页面
 * @constructor
 */
const DriverArchivePage: React.FC = () => {
  const [form] = Form.useForm(); // antd表单实例
  const [searchParams, setSearchParams] = useState({}); // 搜索参数
  const [modalVisible, setModalVisible] = useState(false); // 弹窗可见性
  const [modalMode, setModalMode] = useState<'view' | 'edit'>('view'); // 弹窗模式
  const [selectedRecord, setSelectedRecord] = useState<any>(null); // 当前选中的行
  const [modalLoading, setModalLoading] = useState(false); // 弹窗提交loading状态
  const [linkFaceLibModalVisible, setLinkFaceLibModalVisible] = useState(false); // 关联人脸库弹窗可见性
  const [linkFaceLibRecord, setLinkFaceLibRecord] = useState<any>(null); // 关联人脸库的记录

  // 车队选项
  const { fleetOptions, fetchFleetList } = useFleetOptions();
  const [fleetSearchValue, setFleetSearchValue] = useState(''); // 车队搜索关键词

  // 表格数据hook
  const { page, limit, loading, data, totalCount, onPageChange, getData } = useTable({
    requestFunc: getDriverArchiveList,
    searchParams,
  });

  // 查看
  const handleView = (record: any) => {
    setModalMode('view');
    setSelectedRecord(record);
    setModalVisible(true);
  };
  // 编辑
  const handleEdit = (record: any) => {
    setModalMode('edit');
    setSelectedRecord(record);
    setModalVisible(true);
  };
  // 绑定人脸
  const handleBindFace = (record: any) => {
    setLinkFaceLibRecord(record);
    setLinkFaceLibModalVisible(true);
  };

  // 弹窗关闭
  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedRecord(null);
  };

  // 弹窗确认
  const handleModalOk = async (values: any) => {
    console.log('Form values:', values);

    // 只在编辑模式下调用接口
    if (modalMode === 'edit') {
      setModalLoading(true);
      try {
        // 准备提交的数据，确保包含必要的字段
        const submitData = {
          id: selectedRecord?.id, // 司机档案ID
          driverId: selectedRecord?.driverId, // 司机ID
          name: values.name,
          gender: values.gender,
          birthday: values.birthday,
          placeOfOrigin: values.placeOfOrigin,
          phone: values.phone,
          maritalStat: values.maritalStat,
          fertilityStat: values.fertilityStat,
          drivingAge: values.drivingAge,
          assistDrivingExp: values.assistDrivingExp,
          permResidence: values.permResidence,
          familyEconStat: values.familyEconStat,
          personality: values.personality,
          safetyAware: values.safetyAware,
          vehicleMaintSkill: values.vehicleMaintSkill,
          mandarinAbil: values.mandarinAbil,
          cargoTransQual: values.cargoTransQual,
          ins3yClaimCnt: values.ins3yClaimCnt,
          // 图片字段，转换为JSON字符串
          idCardFile: values.idCardFileUrl && values.idCardFileUrl.length > 0
            ? JSON.stringify(values.idCardFileUrl)
            : undefined,
          drivingLicenseFile: values.driveLicenseFileUrl && values.driveLicenseFileUrl.length > 0
            ? JSON.stringify(values.driveLicenseFileUrl)
            : undefined,
          driverImageFile: values.driverImageFileUrl && values.driverImageFileUrl.length > 0
            ? JSON.stringify(values.driverImageFileUrl)
            : undefined,
          // 其他待添加字段
          ...values,
        };

        // 调用更新接口
        const response = await updateDriverArchiveInfo(submitData);

        if (response.data?.success) {
          message.success('更新成功！');
          setModalVisible(false);
          setSelectedRecord(null);
          getData(); // 刷新表格
        } else {
          message.error(response.data?.msg || '更新失败，请重试');
        }
      } catch (error) {
        console.error('更新司机档案失败:', error);
        message.error('更新失败，请重试');
      } finally {
        setModalLoading(false);
      }
    } else {
      // 查看模式，直接关闭
      setModalVisible(false);
      setSelectedRecord(null);
    }
  };

  // 关联人脸库弹窗关闭
  const handleLinkFaceLibCancel = () => {
    setLinkFaceLibModalVisible(false);
    setLinkFaceLibRecord(null);
  };

  // 关联人脸库弹窗确认
  const handleLinkFaceLibOk = () => {
    setLinkFaceLibModalVisible(false);
    setLinkFaceLibRecord(null);
    getData(); // 刷新表格
  };

  

  // 渲染图片列表
  const renderImageList = (urls: string[] = []) => {
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        {urls.map((url, index) => (
          <Image key={index} src={url} alt={`Image ${index + 1}`} width={100} height={100} />
        ))}
      </div>
    );
  };

  const columns: any[] = [
    { title: '司机姓名', dataIndex: 'name', key: 'name', fixed: 'left', width: 120 },
    { title: '性别', dataIndex: 'gender', key: 'gender', width: 100 },
    { title: '出生日期', dataIndex: 'birthday', key: 'birthday', width: 100 },
    { title: '籍贯', dataIndex: 'placeOfOrigin', key: 'placeOfOrigin', width: 100 },
    { title: '常居住地', dataIndex: 'permResidence', key: 'permResidence', width: 100 },
    { title: '联系电话', dataIndex: 'phone', key: 'phone', width: 100 },
    { title: '学历', dataIndex: 'xxyyEducation', key: 'xxyyEducation', width: 100 },
    {
      title: '身份证',
      dataIndex: 'idCardFileUrl',
      key: 'idCardFileUrl',
      width: 300,
      render: (text: any, record: any) => {
        const urls = record.idCardFileUrl?.map((item: any) => item.ossUrl) || [];
        return renderImageList(urls);
      },
    },
    {
      title: '驾驶证',
      dataIndex: 'driveLicenseFileUrl',
      key: 'driveLicenseFileUrl',
      width: 300,
      render: (text: any, record: any) => {
        const urls = record.driveLicenseFileUrl?.map((item: any) => item.ossUrl) || [];
        return renderImageList(urls);
      },
    },
    {
      title: '照片',
      dataIndex: 'driverImageFileUrl',
      key: 'driverImageFileUrl',
      width: 300,
      render: (text: any, record: any) => {
        const urls = record.driverImageFileUrl?.map((item: any) => item.ossUrl) || [];
        return renderImageList(urls);
      },
    },
    {
      title: '人脸库图片',
      dataIndex: 'faceUrl',
      key: 'faceUrl',
      width: 300,
      render: (text: any, record: any) => renderImageList(record.faceUrl ? [record.faceUrl] : []),
    },
    { title: '婚姻情况', dataIndex: 'maritalStat', key: 'maritalStat', width: 100 },
    { title: '生育情况', dataIndex: 'fertilityStat', key: 'fertilityStat', width: 100 },
    { title: '驾龄', dataIndex: 'drivingAge', key: 'drivingAge', width: 100 },
    { title: '家庭经济情况', dataIndex: 'familyEconStat', key: 'familyEconStat', width: 100 },
    { title: '性格', dataIndex: 'personality', key: 'personality', width: 100 },
    { title: '是否有辅助驾驶设备使用经验', dataIndex: 'assistDrivingExp', key: 'assistDrivingExp', width: 100 },
    { title: '安全意识', dataIndex: 'safetyAware', key: 'safetyAware', width: 100 },
    { title: '最近一次工作日期', dataIndex: 'xxyyLastWorkDate', key: 'xxyyLastWorkDate', width: 100 },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 350,
      render: (text: any, record: any) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleView(record)}>档案查看</Button>
          <Button type="link" onClick={() => handleEdit(record)}>编辑</Button>
          {
             !(record.faceUrl && record.faceUrl.length > 0)&&<Button type="link" onClick={() => handleBindFace(record)}>绑定人脸库</Button>
          }
          
        </Space>
      ),
    },
  ];

  const onSearch = (values: any) => {
    setSearchParams(values);
  };

  // 导出
  const onExport = () => {
  };



  //获取并更新车辆类型
  const [CarType, setCarType] = useState<any>([]); // 车辆类型
  const getCarType = async () => {
    const res = await tagListRequest({
      tagSorts: [TagCategoryEnum.carTypeData],
    });
    const tags = (res.data?.data || []).map((item: any) => {
      return { label: item.title, value: item.title };
    });
    setCarType(tags);
  }
  useEffect(() => {
    getCarType();
  }, []);

  return (
    <Card className={styles.pageContent}>
      <Form form={form} onFinish={onSearch}>
        <Row gutter={24}>
          <Col span={4}>
            <Form.Item name="name" label="司机姓名">
              <Input placeholder="请输入司机姓名" allowClear />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="companyId" label="所属车队">
              <Select
                placeholder="请选择"
                allowClear
                showSearch
                searchValue={fleetSearchValue}
                onSearch={(value) => {
                  setFleetSearchValue(value);
                  fetchFleetList(value);
                }}
                onChange={() => setFleetSearchValue('')}
                filterOption={false}
              >
                {fleetOptions.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="vehicleType" label="车辆类型">
              <Select placeholder='请选择车辆类型' options={CarType} allowClear />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="faceFlag" label="人脸库绑定状态">
              <Select placeholder="请选择" allowClear>
                <Option value={1}>已绑定</Option>
                <Option value={0}>未绑定</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="grade" label="司机评级">
              <Select placeholder="请选择" allowClear>
                <Option value="低风险">低风险</Option>
                <Option value="中风险">中风险</Option>
                <Option value="高风险">高风险</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                搜索
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={onExport}>
                导出
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <CommonTable
        loading={loading}
        columns={columns}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: limit,
          total: totalCount,
          onChange: onPageChange,
        }}
      />
      <DriverDetailModal
        visible={modalVisible}
        mode={modalMode}
        onCancel={handleModalCancel}
        onOk={handleModalOk}
        initialValues={selectedRecord}
        confirmLoading={modalLoading}
      />
      <LinkFaceLibModal
        visible={linkFaceLibModalVisible}
        onCancel={handleLinkFaceLibCancel}
        onOk={handleLinkFaceLibOk}
        record={linkFaceLibRecord}
        fleetOptions={fleetOptions}
        fetchFleetList={fetchFleetList}
      />
    </Card>
  );
};

export default DriverArchivePage;
